import type { AdminPermission } from '@aitoken/contracts';
import {
  Activity,
  Bell,
  Boxes,
  Coins,
  HandCoins,
  Images,
  LayoutDashboard,
  ListVideo,
  Network,
  Ticket,
  Rocket,
  ScrollText,
  Settings,
  ShieldCheck,
  Store,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export type NavPath =
  | '/'
  | '/users'
  | '/quota'
  | '/referrals'
  | '/resellers'
  | '/topup'
  | '/redeem'
  | '/models'
  | '/channels'
  | '/assets'
  | '/tasks'
  | '/alerts'
  | '/audit'
  | '/admins'
  | '/settings'
  | '/system'
  | '/release';

export interface NavItem {
  to: NavPath;
  label: string;
  icon: LucideIcon;
  /** 任一权限即可见 */
  permission: readonly AdminPermission[];
  /** 只在该站点显示（如数字货币充值仅海外站） */
  site?: 'cn' | 'global';
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: '经营',
    items: [
      { to: '/', label: '经营总览', icon: LayoutDashboard, permission: ['dashboard.read'] },
      { to: '/users', label: '用户', icon: Users, permission: ['user.read'] },
      { to: '/quota', label: '额度', icon: Wallet, permission: ['quota.write', 'user.read'] },
      { to: '/referrals', label: '推荐分润', icon: HandCoins, permission: ['user.read'] },
      { to: '/resellers', label: '分销渠道', icon: Store, permission: ['user.read'] },
      { to: '/topup', label: '在线充值', icon: Coins, permission: ['user.read'], site: 'global' },
      { to: '/redeem', label: '兑换码', icon: Ticket, permission: ['user.read'] },
    ],
  },
  {
    label: '供给',
    items: [
      {
        to: '/models',
        label: '模型',
        icon: Boxes,
        permission: ['model.write', 'price.write', 'channel.read'],
      },
      { to: '/channels', label: '渠道', icon: Network, permission: ['channel.read'] },
      { to: '/assets', label: '素材', icon: Images, permission: ['asset.read'] },
    ],
  },
  {
    label: '运行',
    items: [
      { to: '/tasks', label: '任务', icon: ListVideo, permission: ['task.read'] },
      { to: '/alerts', label: '告警', icon: Bell, permission: ['alert.write', 'dashboard.read'] },
      { to: '/audit', label: '审计', icon: ScrollText, permission: ['audit.read'] },
    ],
  },
  {
    label: '系统',
    items: [
      { to: '/admins', label: '管理员', icon: ShieldCheck, permission: ['admin.manage'] },
      { to: '/system', label: '系统健康', icon: Activity, permission: ['settings.write'] },
      { to: '/release', label: '发布', icon: Rocket, permission: ['settings.write'] },
      { to: '/settings', label: '设置', icon: Settings, permission: ['settings.write'] },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

export function navPermission(path: NavPath): readonly AdminPermission[] {
  return NAV_ITEMS.find((i) => i.to === path)?.permission ?? [];
}

/** 菜单项是否可见：有任一权限，且站点匹配 */
export function navVisible(
  me: { permissions: readonly AdminPermission[]; site_id: 'cn' | 'global' } | null | undefined,
  item: NavItem | undefined,
): boolean {
  if (!me || !item) return false;
  if (item.site && item.site !== me.site_id) return false;
  return item.permission.some((p) => me.permissions.includes(p));
}

export const navItem = (path: NavPath) => NAV_ITEMS.find((i) => i.to === path);
